import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import Modal from '../../../components/Modal/Modal';
// import '../../styles/challengerequests.css';
import API_URL from "../../../config/config";
import axios from "axios";

let transactionsListCache = null;

function Transactions() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(() => transactionsListCache?.currentPage || 1);
    const [itemsPerPage, setItemsPerPage] = useState(() => transactionsListCache?.itemsPerPage || 50);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState(
        () =>
            transactionsListCache?.filters || {
                entityType: "",
                action: "",
                performedBy: ""
            }
    );
    const [restoreId, setRestoreId] = useState(null);
    const [deleteTransId, setDeleteTransId] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isRestoring, setIsRestoring] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const token = localStorage.getItem("adminToken");
    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

    const initialScrollYRef = useRef(transactionsListCache?.scrollY || 0);
    const scrollRestoredRef = useRef(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage,
                limit: itemsPerPage
            };
            if (filters.entityType) params.entityType = filters.entityType;
            if (filters.action) params.action = filters.action;
            if (filters.performedBy) params.performedBy = filters.performedBy;

            const response = await axios.get(`${API_URL}/transactions`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data.success && response.data.transactions) {
                setData(response.data.transactions || []);
                setTotalItems(response.data.total || 0);
            } else {
                setData([]);
                setTotalItems(0);
            }
        } catch (err) {
            console.error("Error fetching transactions:", err.response?.data || err.message);
            setError(err.response?.data?.message || "Unable to fetch transaction data.");
            setData([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [token, currentPage, itemsPerPage, filters]);

    useEffect(() => {
        if (token) {
            fetchData();
        }
    }, [token, currentPage, itemsPerPage, filters, fetchData]);

    useEffect(() => {
        transactionsListCache = {
            ...(transactionsListCache || {}),
            currentPage,
            itemsPerPage,
            filters
        };
    }, [currentPage, itemsPerPage, filters]);

    useEffect(() => {
        return () => {
            transactionsListCache = {
                ...(transactionsListCache || {}),
                scrollY: window.scrollY || 0,
            };
        };
    }, []);

    useEffect(() => {
        if (loading) return;
        if (scrollRestoredRef.current) return;
        scrollRestoredRef.current = true;
        const y = Number(initialScrollYRef.current || 0);
        if (y > 0) {
            requestAnimationFrame(() => window.scrollTo(0, y));
        }
    }, [loading]);

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const renderPaginationButtons = () => {
        const pages = [];
        const totalNumbersToShow = 5;
        const half = Math.floor(totalNumbersToShow / 2);

        let startPage = Math.max(currentPage - half, 1);
        let endPage = Math.min(currentPage + half, totalPages);

        if (currentPage <= half) {
            endPage = Math.min(totalNumbersToShow, totalPages);
        }

        if (currentPage > totalPages - half) {
            startPage = Math.max(totalPages - totalNumbersToShow + 1, 1);
        }

        if (totalPages < totalNumbersToShow) {
            startPage = 1;
            endPage = totalPages;
        }

        if (startPage > 1) {
            pages.push(
                <button key={1} className={`paginate_button ${currentPage === 1 ? "current active" : ""}`} onClick={() => setCurrentPage(1)}>
                    1
                </button>
            );
            if (startPage > 2) {
                pages.push(<span key="start-ellipsis" className="ellipsis">...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`paginate_button ${currentPage === i ? "current active" : ""}`}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="end-ellipsis" className="ellipsis">...</span>);
            }
            pages.push(
                <button key={totalPages} className={`paginate_button ${currentPage === totalPages ? "current active" : ""}`} onClick={() => setCurrentPage(totalPages)}>
                    {totalPages}
                </button>
            );
        }

        return pages;
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setCurrentPage(1);
    };

    const handleRestore = async () => {
        if (!restoreId) return;
        const idToRestore = restoreId;
        setRestoreId(null);
        setIsRestoring(true);
        try {
            const res = await axios.post(`${API_URL}/transactions/${idToRestore}/restore`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage(res.data.message || "Provider restored successfully");
            setErrorMessage("");
            fetchData(); // Refresh the list
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Failed to restore provider");
            setSuccessMessage("");
            setTimeout(() => setErrorMessage(""), 5000);
        } finally {
            setIsRestoring(false);
        }
    };

    const handleDeleteTransaction = async () => {
        if (!deleteTransId) return;
        const idToDelete = deleteTransId;
        setDeleteTransId(null);
        setIsDeleting(true);
        try {
            const res = await axios.delete(`${API_URL}/transactions/${idToDelete}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage(res.data.message || "Transaction deleted permanently");
            setErrorMessage("");
            fetchData(); // Refresh the list
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Failed to delete transaction");
            setSuccessMessage("");
            setTimeout(() => setErrorMessage(""), 5000);
        } finally {
            setIsDeleting(false);
        }
    };

    const numTableColumns = 10;

    return (
        <>
            <Helmet>
                <title>Admin | Transactions</title>
            </Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">Transactions</div>
                            </div>
                        </div>
                        <div className='container-fluid'>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='card'>
                                        <div className="card-header">
                                            <div>Deletion Transactions </div>
                                        </div>
                                        <div className='card-body'>
                                            {successMessage && (
                                                <div className="alert alert-success alert-dismissible fade show" role="alert" style={{ backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" }}>
                                                    <strong>Success!</strong> {successMessage}
                                                    <button type="button" className="btn-close" onClick={() => setSuccessMessage("")}></button>
                                                </div>
                                            )}
                                            {errorMessage && (
                                                <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" }}>
                                                    <strong>Error!</strong> {errorMessage}
                                                    <button type="button" className="btn-close" onClick={() => setErrorMessage("")}></button>
                                                </div>
                                            )}
                                            <div id='transactions-table_wrapper' className='dataTables_wrapper no-footer'>
                                                <div className='row mb-3'>
                                                    <div className='col-md-3'>
                                                        <label>Entity Type:</label>
                                                        <select
                                                            className="form-control"
                                                            value={filters.entityType}
                                                            onChange={(e) => handleFilterChange("entityType", e.target.value)}
                                                        >
                                                            <option value="">All</option>
                                                            <option value="Provider">Provider</option>
                                                            <option value="Blog">Blog</option>
                                                            <option value="User">User</option>
                                                        </select>
                                                    </div>
                                                    <div className='col-md-3'>
                                                        <label>Action:</label>
                                                        <select
                                                            className="form-control"
                                                            value={filters.action}
                                                            onChange={(e) => handleFilterChange("action", e.target.value)}
                                                        >
                                                            <option value="">All</option>
                                                            <option value="soft_delete">Soft Delete</option>
                                                            <option value="delete">Delete</option>
                                                            <option value="restore">Restore</option>
                                                        </select>
                                                    </div>
                                                    <div className='col-md-3'>
                                                        <label>Performed By:</label>
                                                        <select
                                                            className="form-control"
                                                            value={filters.performedBy}
                                                            onChange={(e) => handleFilterChange("performedBy", e.target.value)}
                                                        >
                                                            <option value="">All</option>
                                                            <option value="admin">Admin</option>
                                                            <option value="frontend">Frontend</option>
                                                            <option value="system">System</option>
                                                        </select>
                                                    </div>
                                                    <div className='col-md-3'>
                                                        <label>Show</label>
                                                        <select
                                                            value={itemsPerPage}
                                                            onChange={(e) => {
                                                                setItemsPerPage(Number(e.target.value));
                                                                setCurrentPage(1);
                                                            }}
                                                            className="form-control"
                                                        >
                                                            <option value="25">25</option>
                                                            <option value="50">50</option>
                                                            <option value="100">100</option>
                                                            <option value="200">200</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='table-responsive'>
                                                    <table className="table table-bordered dataTable no-footer overflow-x-auto mt-2" id="transactions-table">
                                                        <thead>
                                                            <tr role="row">
                                                                <th>No.</th>
                                                                <th>Entity Type</th>
                                                                <th>Provider Name</th>
                                                                <th>Entity ID</th>
                                                                <th>Action</th>
                                                                <th>Performed By</th>
                                                                <th>Admin Email</th>
                                                                <th>Status</th>
                                                                <th>Date & Time</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {loading ? (
                                                                Array.from({ length: itemsPerPage }).map((_, rowIndex) => (
                                                                    <tr key={`skeleton-${rowIndex}`}>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                        <td><div className="skeletant-bx skeletant-design sh-47"></div></td>
                                                                    </tr>
                                                                ))
                                                            ) : error ? (
                                                                <tr>
                                                                    <td colSpan={numTableColumns} style={{ textAlign: "center", padding: "10px", color: "red" }}>{error}</td>
                                                                </tr>
                                                            ) : data.length > 0 ? (
                                                                data.map((row, index) => (
                                                                    <tr key={row._id || row.id}>
                                                                        <td>{itemsPerPage * (currentPage - 1) + index + 1}</td>
                                                                        <td>{row.entityType || 'N/A'}</td>
                                                                        <td>
                                                                            <strong>
                                                                                {row.entityData?.name || 'N/A'}
                                                                            </strong>
                                                                        </td>
                                                                        <td>
                                                                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                                                {row.entityId?.toString() || 'N/A'}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className={`badge ${row.action === 'soft_delete' ? 'bg-warning' : row.action === 'delete' ? 'bg-danger' : row.action === 'restore' ? 'bg-success' : 'bg-secondary'}`}>
                                                                                {row.action || 'N/A'}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className={`badge ${row.performedBy === 'admin' ? 'bg-primary' : row.performedBy === 'frontend' ? 'bg-info' : 'bg-secondary'}`}>
                                                                                {row.performedBy || 'N/A'}
                                                                            </span>
                                                                        </td>
                                                                        <td>{row.adminEmail || '-'}</td>
                                                                        <td>
                                                                            <span className={`badge ${row.status === 'success' ? 'bg-success' : row.status === 'failed' ? 'bg-danger' : 'bg-warning'}`}>
                                                                                {row.status || 'N/A'}
                                                                            </span>
                                                                        </td>
                                                                        <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : 'N/A'}</td>
                                                                        <td>
                                                                            {row.action === 'soft_delete' && (
                                                                                <div style={{ display: 'flex', gap: '5px', minWidth: '130px' }}>
                                                                                    <button
                                                                                        className="btn btn-sm btn-success"
                                                                                        title={`Restore ${row.entityType}`}
                                                                                        onClick={() => setRestoreId(row._id)}
                                                                                        disabled={isRestoring || isDeleting}
                                                                                        style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                                    >
                                                                                        Restore
                                                                                    </button>
                                                                                    <button
                                                                                        className="btn btn-sm btn-danger"
                                                                                        title="Delete Transaction"
                                                                                        onClick={() => setDeleteTransId(row._id)}
                                                                                        disabled={isRestoring || isDeleting}
                                                                                        style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                                    >
                                                                                        Delete
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={numTableColumns} style={{ textAlign: "center", padding: "10px" }}>No transaction data available</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div className="dataTables_info">
                                                    Showing {totalItems === 0 ? 0 : (itemsPerPage * (currentPage - 1)) + 1} to{" "}
                                                    {Math.min(itemsPerPage * currentPage, totalItems)} of {totalItems} entries
                                                </div>

                                                <div className="dataTables_paginate paging_simple_numbers">
                                                    <Link to="#" className={`paginate_button previous ${currentPage === 1 ? "disabled" : ""}`} onClick={(e) => { e.preventDefault(); setCurrentPage((prev) => Math.max(prev - 1, 1)); }}>Previous</Link>
                                                    <span>
                                                        {renderPaginationButtons()}
                                                    </span>
                                                    <Link to="#" className={`paginate_button next ${currentPage === totalPages ? "disabled" : ""}`} onClick={(e) => { e.preventDefault(); setCurrentPage((prev) => Math.min(prev + 1, totalPages)); }}>Next</Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>
            <Modal
                isOpen={restoreId !== null}
                onClose={() => setRestoreId(null)}
                onConfirm={handleRestore}
                title="Restore Provider"
                message="Are you sure you want to restore this provider? It will be visible again in the admin panel and frontend."
                confirmText="Restore"
                cancelText="Cancel"
            />
            <Modal
                isOpen={deleteTransId !== null}
                onClose={() => setDeleteTransId(null)}
                onConfirm={handleDeleteTransaction}
                title="Delete Transaction"
                message="Are you sure you want to permanently delete this transaction from the recycle bin? This action cannot be undone."
                confirmText="Delete Permanently"
                cancelText="Cancel"
            />
        </>
    );
}

export default Transactions;
